import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../providers/AuthProvider";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import api from "../../services/api/axios";
import { adminDropdownSx, adminDropdownMenuProps } from "./adminDropdownStyles";
import { BRAND_BLUE } from "../../theme/colors";
import AdminOrgInvitations from "./AdminOrgInvitations";

export default function AdminOrgUsers() {
  const { token, user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);
  const [busyAdminUserId, setBusyAdminUserId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load organizations
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/organizations", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrgs(res.data || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load organizations");
      }
    })();
  }, [token]);

  const canLoadUsers = String(orgId || "").length > 0;

  const loadUsers = async () => {
    if (!canLoadUsers) return;
    setError("");
    setSuccess("");
    setLoading(true);
    setSelected([]);
    try {
      const res = await api.get(`/admin/organizations/${orgId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canLoadUsers) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const allChecked = useMemo(
    () => users.length > 0 && selected.length === users.length,
    [users, selected],
  );

  const indeterminate = useMemo(
    () => selected.length > 0 && selected.length < users.length,
    [users, selected],
  );

  const toggleAll = (e) => {
    if (e.target.checked) setSelected(users.map((u) => u.id));
    else setSelected([]);
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const deleteSelected = async () => {
    setError("");
    setSuccess("");
    setBusyDelete(true);
    try {
      const payload = { user_ids: selected };
      const res = await api.post(
        `/admin/organizations/${orgId}/users/batch-delete`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const deleted = res?.data?.deleted ?? 0;
      setSuccess(`Deleted ${deleted} user${deleted === 1 ? "" : "s"}.`);
      await loadUsers();
      setSelected([]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete users");
    } finally {
      setBusyDelete(false);
    }
  };

  const deleteOne = async (userId) => {
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/organizations/${orgId}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("User deleted.");
      await loadUsers();
      setSelected((prev) => prev.filter((id) => id !== userId));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  const updateAdminAccess = async (targetUser, isAdmin) => {
    setError("");
    setSuccess("");
    setBusyAdminUserId(targetUser.id);
    try {
      const res = await api.patch(
        `/admin/users/${targetUser.id}`,
        { is_admin: isAdmin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data;
      setUsers((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      setSuccess(
        isAdmin
          ? `Granted admin access to ${targetUser.email}.`
          : `Removed admin access from ${targetUser.email}.`,
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update admin access");
    } finally {
      setBusyAdminUserId(null);
    }
  };

  return (
    <Box sx={{ p: 3, display: "grid", gap: 2 }}>
      <Paper
        sx={{ p: 2, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6" sx={{ color: BRAND_BLUE }}>
            Manage Organization Users
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh users">
            <span>
              <IconButton
                onClick={loadUsers}
                disabled={!canLoadUsers || loading}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="org-select">Organization</InputLabel>
          <Select
            sx={adminDropdownSx}
            MenuProps={adminDropdownMenuProps}
            labelId="org-select"
            value={orgId}
            label="Organization"
            onChange={(e) => setOrgId(e.target.value)}
          >
            {orgs.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />}
                    checked={allChecked}
                    indeterminate={indeterminate}
                    onChange={toggleAll}
                    disabled={users.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>Email</TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>Name</TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>Admin</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />}
                      checked={selected.includes(u.id)}
                      onChange={() => toggleOne(u.id)}
                    />
                  </TableCell>

                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.name || "—"}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        u.id === user?.id && u.is_admin
                          ? "You cannot remove your own admin access here"
                          : u.is_admin
                            ? "Remove admin access"
                            : "Grant admin access"
                      }
                    >
                      <span>
                        <Switch
                          checked={Boolean(u.is_admin)}
                          onChange={(e) => updateAdminAccess(u, e.target.checked)}
                          disabled={
                            busyAdminUserId === u.id ||
                            (u.id === user?.id && u.is_admin)
                          }
                          size="small"
                          inputProps={{
                            "aria-label": `Admin access for ${u.email}`,
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete user">
                      <span>
                        <IconButton
                          onClick={() => deleteOne(u.id)}
                          disabled={!orgId || busyDelete}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {canLoadUsers
                      ? loading
                        ? "Loading..."
                        : "No users found."
                      : "Select an organization"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2 }}
          justifyContent="space-between"
        >
          <Typography variant="body2">{selected.length} selected</Typography>
          <Button
            variant="contained"
            color="error"
            onClick={deleteSelected}
            disabled={selected.length === 0 || busyDelete}
            startIcon={<DeleteIcon />}
          >
            Delete selected
          </Button>
        </Stack>
      </Paper>
      <AdminOrgInvitations
        organizationId={orgId}
        organizationName={orgs.find((org) => String(org.id) === String(orgId))?.name || ""}
      />
    </Box>
  );
}
